<template>
    <div class="form-builder vfb">
        <Snippet v-if="responseHtml" class="vfb__response" :renderedHtml="responseHtml" />

        <div v-if="submitting && parsedFormSubmit.ajax && !formHidden" class="vfb-loading-overlay" aria-live="polite" aria-busy="true">
            <div class="form-builder-loading__content">
                <div class="form-builder-loading__spinner">
                    <div class="form-builder-loading__dot form-builder-loading__dot--1"></div>
                    <div class="form-builder-loading__dot form-builder-loading__dot--2"></div>
                    <div class="form-builder-loading__dot form-builder-loading__dot--3"></div>
                </div>
                <p class="form-builder-loading__text">Loading...</p>
            </div>
        </div>

        <FormContainer
            v-show="!formHidden"
            validation-mode="always"
            :validation-messages="parsedValidationMessages"
            :action="form_action"
            method="POST"
            ref="formContainerRef"
            :ajax="parsedFormSubmit.ajax"
            @form-submit="onSubmitStart"
            @submit-aborted="onSubmitAborted"
            @ajax-success="onAjaxSuccess"
            @ajax-error="onAjaxError"
            @hashcash-success="() => emit('vfb-submit', {
                form_id: props.form_id,
                form_name: props.form_name ?? null,
                form_lang: props.form_lang ?? null,
            })"
        >
            <input type="hidden" name="form_id" :value="props.form_id">

            <div class="row gx-4 gy-4 vfb__grid">
                <template v-for="block in renderBlocks"
                          :key="block.kind === 'fieldset' ? 'fs-' + block.head.id : 'el-' + block.index">
                    <div v-if="block.kind === 'single' && isElementVisible(block.element)"
                         :class="'col-md-'+block.element.columnSize" class="col-12 vfb__column">
                        <div class="vfb__element">
                            <component
                                :is="getComponent(block.element.type)"
                                v-if="getComponent(block.element.type)"
                                v-bind="getElementProps(block.element, block.index)"
                                @update:modelValue="(value) => block.element.name && updateFieldValue(block.element.name, value)"
                            />
                        </div>
                    </div>
                    <div v-else-if="block.kind === 'fieldset' && isElementVisible(block.head)"
                         :class="'col-md-'+(block.head.columnSize || 12)" class="col-12 vfb__column">
                        <div class="vfb__element">
                            <RepeatableFieldset
                                :head="block.head"
                                :members="block.members"
                                :resolve-component="getComponent"
                                :build-member-props="buildMemberProps"
                                :on-member-update="onMemberUpdate"
                                :on-instances-changed="refreshValidator"
                                :on-instance-removed="reindexFieldsetValues"
                                :add-label-fallback="parsedFieldsetLabels.add"
                                :remove-label-fallback="parsedFieldsetLabels.remove"
                            />
                        </div>
                    </div>
                </template>
            </div>

            <div class="mt-4 vfb__actions">
                <SubmitButton
                    :text="parsedFormSubmit.text"
                    :alignButton="parsedFormSubmit.alignButton"
                    :color="parsedFormSubmit.color"
                    :classes="parsedFormSubmit.classes"
                    :disabled="submitting"/>
            </div>

        </FormContainer>

    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue';
import type { FormBuilderProps, FormBuilderSubmitPayload } from './utils/types';
import Spacer from './components/AdditionalFields/Spacer.ce.vue';
import Title from './components/AdditionalFields/Title.ce.vue';
import Subtitle from './components/AdditionalFields/Subtitle.ce.vue';
import Text from './components/AdditionalFields/Text.ce.vue';
import SmallTextField from './components/AdditionalFields/SmallTextField.ce.vue';
import Control from './components/FormElements/Control.ce.vue';
import TextArea from './components/FormElements/TextArea.ce.vue';
import Select from './components/FormElements/Select.ce.vue';
import Check from './components/FormElements/Check.ce.vue';
import CheckboxGroup from './components/FormElements/CheckboxGroup.ce.vue';
import RadioGroup from './components/FormElements/RadioGroup.ce.vue';
import Date from './components/FormElements/Date.ce.vue';
import File from './components/FormElements/File.ce.vue';
import RepeatableFieldset from './components/FormElements/RepeatableFieldset.ce.vue';
import SubmitButton from './components/AdditionalFormUtils/SubmitButton.ce.vue';
import FormContainer from './components/Form/FormContainer.ce.vue';
import {getFormFieldTypes, getAdditionalFieldTypes, loadFieldOverrides} from './utils/merge-fields.js';
import Divider from './components/AdditionalFields/Divider.ce.vue';
import Snippet from './components/AdditionalFields/Snippet.ce.vue';
import { evaluateConditions } from './utils/conditions';

// Component mapping for dynamic rendering
const componentMap = {
  'spacer': Spacer,
  'divider': Divider,
  'title': Title,
  'subtitle': Subtitle,
  'textfield': Text,
  'small-textfield': SmallTextField,
  'text': Control,
  'email': Control,
  'number': Control,
  'textarea': TextArea,
  'select': Select,
  'checkbox': Check,
  'checkbox-group': CheckboxGroup,
  'radio-group': RadioGroup,
  'date': Date,
    'file': File,
  'snippet': Snippet
};

const getComponent = (elementType: string) => {
  return componentMap[elementType] || null;
};

const toNumber = (value: unknown, fallback?: number) => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeElement = (element: any) => {
  switch (element.type) {
    case 'title':
    case 'subtitle':
    case 'textfield':
    case 'small-textfield':
      return {
        ...element,
        alignText: element.alignText ?? 'left'
      };
    case 'text':
    case 'email':
    case 'number':
      return {
        ...element,
        value: element.value ?? '',
        placeholder: element.placeholder ?? '',
        helpText: element.helpText ?? ''
      };
    case 'textarea':
      return {
        ...element,
        value: element.value ?? '',
        placeholder: element.placeholder ?? '',
        helpText: element.helpText ?? '',
        rows: toNumber(element.rows, 6)
      };
    case 'checkbox':
      return {
        ...element,
        checked: element.checked ?? false,
        helpText: element.helpText ?? '',
        value: element.value ?? true
      };
    case 'select':
    case 'radio-group':
      return {
        ...element,
        helpText: element.helpText ?? ''
      };
    case 'checkbox-group':
      return {
        ...element,
        helpText: element.helpText ?? '',
        value: element.value ?? []
      };
    case 'fieldset':
      return {
        ...element,
        minInstances: toNumber(element.minInstances, 1),
        maxInstances: toNumber(element.maxInstances, 0),
        columnSize: element.columnSize ?? 12
      };
    default:
      return element;
  }
};

const normalizeFormSubmit = (formSubmit: any) => ({
  text: formSubmit?.text ?? 'Submit',
  alignButton: formSubmit?.alignButton ?? 'left',
  color: formSubmit?.color ?? 'primary',
  classes: formSubmit?.classes ?? 'btn btn-primary w-100',
  // True when a success snippet is configured → submit via Ajax. Accept both a
  // real boolean and the "true"/"false" string from the web-component attribute.
  ajax: formSubmit?.ajax === true || formSubmit?.ajax === 'true'
});

// Element types whose outer wrapper is bound to a non-`wrapper` classes key.
// Everything else (all data-bearing fields, textfield, small-textfield) uses `wrapper`.
const WRAPPER_KEY_BY_TYPE: Record<string, string> = {
  title: 'title',
  subtitle: 'title',
  spacer: 'spacer',
  divider: 'divider',
};

// Merge a per-instance custom CSS class into the element's outer-wrapper
// class key. No-op when empty. The value is intentionally NOT forwarded as a
// prop (see buildElementProps) so it never appears as a stray `cssclass`
// fallthrough attribute on the rendered DOM node.
const applyCustomClass = (type: string, elementProps: any, cssClass?: string) => {
  const extra = (cssClass ?? '').trim();
  if (!extra || !elementProps || typeof elementProps.classes !== 'object') {
    return elementProps;
  }
  const key = WRAPPER_KEY_BY_TYPE[type] ?? 'wrapper';
  return {
    ...elementProps,
    classes: {
      ...elementProps.classes,
      [key]: [elementProps.classes[key], extra].filter(Boolean).join(' '),
    },
  };
};

// Builds the submission key (HTML name attribute) for a fieldset member.
// Members are submitted as a nested array `fieldsetName[i][localName]` so PHP
// parses them into `$_POST[fieldsetName][i][localName]`. For checkbox-groups
// the trailing "[]" is preserved after the local name (`fieldsetName[i][name][]`)
// so PHP collects multiple values into an array.
const bracketName = (fieldsetName: string | undefined, instanceIndex: number, localName: string | undefined) => {
  if (!fieldsetName || !localName) {
    return localName;
  }
  if (localName.endsWith('[]')) {
    return `${fieldsetName}[${instanceIndex}][${localName.slice(0, -2)}][]`;
  }
  return `${fieldsetName}[${instanceIndex}][${localName}]`;
};

const buildElementProps = (rawElement: any, index: number, instanceIndex = 0, fieldsetName?: string) => {
  // For fieldset members, clone the element with a bracketed name AND a suffixed
  // id so every downstream `element.name`/`element.id` read uses it (the
  // element's own `id` would otherwise be spread over baseProps.id, colliding
  // across instances). Standalone elements pass no fieldsetName → no-op clone,
  // keeping behaviour byte-identical for non-fieldset forms.
  const isMember = !!fieldsetName;
  const element = !isMember
    ? rawElement
    : {
        ...rawElement,
        name: bracketName(fieldsetName, instanceIndex, rawElement.name),
        id: rawElement.id ? `${rawElement.id}__inst${instanceIndex}` : rawElement.id,
      };

  // cssClass/fieldsetId/conditions are handled centrally and must not be spread
  // into the props (would leak as `cssclass`/`fieldsetid`/`conditions`
  // fallthrough attributes on the rendered DOM node).
  const { cssClass: _cssClass, fieldsetId: _fieldsetId, conditions: _conditions, ...elementWithoutCssClass } = element;
  const baseProps = {
    id: !isMember ? `element-${index}` : `element-${index}__inst${instanceIndex}`,
    ...elementWithoutCssClass
  };

  // Special handling for specific element types
  switch (element.type) {
    case 'spacer':
      return {
        spacerSize: element.spacerSize,
        classes: getClassesForAdditionalFieldType(element.type)
      };
    case 'title':
      return {
        ...baseProps,
        title: element.title,
        alignText: element.alignText ?? 'left',
        elementType: getTagTypeForAdditionalFieldType(element.type),
        classes: getClassesForAdditionalFieldType(element.type)
      };
    case 'subtitle':
      return {
        ...baseProps,
        title: element.title,
        alignText: element.alignText ?? 'left',
        elementType: getTagTypeForAdditionalFieldType(element.type),
        classes: getClassesForAdditionalFieldType(element.type)
      };
    case 'textfield':
      return {
        ...baseProps,
        text: element.text,
        alignText: element.alignText ?? 'left',
        classes: getClassesForAdditionalFieldType(element.type)
      };
    case 'small-textfield':
      return {
        ...baseProps,
        text: element.text,
        alignText: element.alignText ?? 'left',
        classes: getClassesForAdditionalFieldType(element.type)
      };
    case 'text':
    case 'email':
    case 'number':
      return {
        ...baseProps,
        label: element.label,
        name: element.name,
        required: isElementRequired(element),
        modelValue: getPrefillValue(element.name, element.value ?? ''),
        helpText: element.helpText ?? undefined,
        type: element.type,
        placeholder: element.placeholder ?? '',
        classes: getClassesForFieldType(element.type)
      };
    case 'textarea':
      return {
        ...baseProps,
        label: element.label,
        name: element.name,
        required: isElementRequired(element),
        modelValue: getPrefillValue(element.name, element.value ?? ''),
        placeholder: element.placeholder ?? '',
        helpText: element.helpText ?? undefined,
        rows: toNumber(element.rows, 6),
        classes: getClassesForFieldType(element.type)
      };
    case 'select':
      return {
        ...baseProps,
        label: element.label,
        name: element.name,
        options: element.options,
        required: isElementRequired(element),
        helpText: element.helpText ?? undefined,
        modelValue: getPrefillValue(element.name, element.defaultOption),
        classes: getClassesForFieldType(element.type)
      };
    case 'checkbox':
      return {
        ...baseProps,
        label: element.label,
        name: element.name,
        required: isElementRequired(element),
        disabled: element.disabled,
        helpText: element.helpText ?? undefined,
        // Seed the checked state into formFieldValues (like every other field
        // type) so conditions referencing this checkbox evaluate correctly on
        // the very first render, before any user interaction.
        modelValue: getPrefillValue(element.name, element.checked ?? false),
        classes: getClassesForFieldType(element.type),
        value: element.value ?? true
      };
    case 'checkbox-group':
      return {
        ...baseProps,
        label: element.label,
        name: element.name,
        options: element.options || [],
        required: isElementRequired(element),
        disabled: element.disabled,
        helpText: element.helpText ?? undefined,
        modelValue: getPrefillValue(element.name, element.value || []),
        layout: element.layout || 'horizontal',
        classes: getClassesForFieldType(element.type)
      };
    case 'radio-group':
      return {
        ...baseProps,
        label: element.label,
        name: element.name,
        options: element.options || [],
        required: isElementRequired(element),
        disabled: element.disabled,
        helpText: element.helpText ?? undefined,
        modelValue: getPrefillValue(element.name, element.defaultOption || ''),
        layout: element.layout || 'vertical',
        classes: getClassesForFieldType(element.type)
      };
    case 'date':
      return {
        ...baseProps,
        label: element.label,
        name: element.name,
        required: isElementRequired(element),
        disabled: element.disabled,
        helpText: element.helpText ?? undefined,
        modelValue: element.value ?? '',
        placeholder: element.placeholder ?? '',
        mode: element.mode || 'single',
        classes: getClassesForFieldType(element.type),
        locale: props.form_lang,
      };
    case 'file':
      return {
        ...baseProps,
        label: element.label,
        name: element.name,
        required: isElementRequired(element),
        disabled: element.disabled,
        helpText: element.helpText ?? undefined,
        acceptTypes: element.acceptTypes,
        multiple: element.multiple || false,
        removeLabel: parsedFileLabels.value.remove,
        uploadLabel: parsedFileLabels.value.upload,
        maxFiles: element.maxFiles,
        maxFileSizeKb: element.maxFileSizeKb,
        dragAndDrop: element.dragAndDrop || false,
        classes: getClassesForFieldType(element.type),
      };
    case 'divider':
      return {
        ...baseProps,
        classes: getClassesForAdditionalFieldType(element.type)
      };
    case 'snippet':
      return {
        renderedHtml: element.renderedHtml ?? ''
      };
    default:
      return baseProps;
  }
};

const getElementProps = (element: any, index: number, instanceIndex = 0, fieldsetName?: string) => {
  return applyCustomClass(element.type, buildElementProps(element, index, instanceIndex, fieldsetName), element.cssClass);
};

// --- Conditions (bedingte Sichtbarkeit / bedingt-required) -----------------
// A field with a `visible` condition is only rendered while it matches; a field
// with a `required` condition is required exactly when it matches (the static
// `required` flag is then ignored — the condition is authoritative). Both read
// the live formFieldValues, so they re-evaluate reactively as the user types.
const isElementVisible = (element: any): boolean => {
  const c = element?.conditions;
  if (c && c.action === 'visible') {
    return evaluateConditions(c, formFieldValues.value);
  }
  return true;
};
const isElementRequired = (element: any): boolean => {
  const c = element?.conditions;
  if (c && c.action === 'required') {
    return evaluateConditions(c, formFieldValues.value);
  }
  // 'not-required' relaxes a statically-required field to optional on match.
  if (c && c.action === 'not-required') {
    return !evaluateConditions(c, formFieldValues.value);
  }
  return !!element?.required;
};

// Bridges the RepeatableFieldset component to the central prop/update helpers.
// The fieldset name (head.name) is supplied by the component so the member's
// bracketed submission key can be composed.
const buildMemberProps = (member: { element: any; index: number }, instanceIndex: number, fieldsetName: string) =>
  getElementProps(member.element, member.index, instanceIndex, fieldsetName);

const onMemberUpdate = (member: { element: any; index: number }, instanceIndex: number, value: any, fieldsetName: string) => {
  const name = bracketName(fieldsetName, instanceIndex, member.element.name);
  if (name) {
    updateFieldValue(name, value);
  }
};

// Beim Entfernen einer Fieldset-Instanz an `removedPosition` werden die
// verbleibenden höheren Instanzen lückenlos um eins heruntergezählt
// (contiguous re-index). formFieldValues ist nach dem Bracket-Submission-Namen
// `fieldsetName[i][localName]` gekeyt und reaktiv an modelValue gebunden — die
// Keys müssen deshalb mitziehen: Werte der entfernten Instanz fallen weg, jede
// höhere Instanz bekommt ihren Index dekrementiert. Sonst zeigt die
// nachrückende Instanz den (veralteten) Wert der gelöschten an.
const reindexFieldsetValues = (fieldsetName: string, removedPosition: number) => {
  if (!fieldsetName) {
    return;
  }
  const escaped = fieldsetName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const keyPattern = new RegExp(`^${escaped}\\[(\\d+)\\](.*)$`);
  const next: Record<string, any> = {};
  for (const [key, value] of Object.entries(formFieldValues.value)) {
    const match = key.match(keyPattern);
    if (!match) {
      next[key] = value;
      continue;
    }
    const index = Number(match[1]);
    if (index === removedPosition) {
      continue; // Werte der entfernten Instanz verwerfen
    }
    const newIndex = index > removedPosition ? index - 1 : index;
    next[`${fieldsetName}[${newIndex}]${match[2]}`] = value;
  }
  formFieldValues.value = next;
};

const props = defineProps<FormBuilderProps>();

const emit = defineEmits<{
    (e: 'vfb-submit', payload: FormBuilderSubmitPayload): void;
}>();

// Ajax submit response state. The server returns ready-to-inject HTML: the
// success snippet (on success → hide the form, show the snippet) or the
// rendered validation errors (on error → keep the form, show the errors).
const responseHtml = ref('');
const formHidden = ref(false);

// True zwischen einem validen Submit und dem Ende der Absendung. Sperrt den
// Submit-Button (beide Modi) und blendet im Ajax-Modus zusätzlich das
// Loading-Overlay ein — gleiche Optik wie das Symfony-UX-Mount-Overlay. Im
// klassischen Modus navigiert die Seite weg, der Zustand bleibt bis dahin.
const submitting = ref(false);

const onSubmitStart = () => {
    submitting.value = true;
};
const onSubmitAborted = () => {
    submitting.value = false;
};
const onPageShow = (evt: Event) => {
    if ((evt as PageTransitionEvent).persisted) {
        submitting.value = false;
    }
};
const onAjaxSuccess = (html: string) => {
    // submitting stays true: the form is replaced by the success snippet, so the
    // submit button must never become clickable again. formHidden drops the
    // loading overlay.
    responseHtml.value = html;
    formHidden.value = true;
};
const onAjaxError = (html: string) => {
    submitting.value = false;
    responseHtml.value = html;
    formHidden.value = false;
};

// Computed properties to handle string to object/array conversion for web components
const parsedFormElements = computed(() => {
    if (typeof props.form_elements === 'string') {
        try {
            const parsedElements = JSON.parse(props.form_elements);
            return Array.isArray(parsedElements) ? parsedElements.map(normalizeElement) : [];
        } catch (error) {
            console.error('Error parsing form_elements:', error);
            return [];
        }
    }
    return Array.isArray(props.form_elements) ? props.form_elements.map(normalizeElement) : [];
});

// Groups the flat element array into render blocks: standalone elements and
// fieldset groups. Membership is resolved via `fieldsetId` (referencing the
// head element's `id`), NOT array position. Defensive: an element pointing at
// an unknown fieldsetId, or a fieldset head without an id, renders standalone
// so a malformed schema never drops fields.
const renderBlocks = computed(() => {
    const elements = parsedFormElements.value;
    const heads = new Map<string, any>();
    elements.forEach((element) => {
        if (element.type === 'fieldset' && element.id) {
            heads.set(element.id, element);
        }
    });

    const blocks: Array<
        | { kind: 'single'; element: any; index: number }
        | { kind: 'fieldset'; head: any; members: Array<{ element: any; index: number }>; index: number }
    > = [];
    const seenFieldsetIds = new Set<string>();

    elements.forEach((element, index) => {
        if (element.type === 'fieldset') {
            if (!element.id || seenFieldsetIds.has(element.id)) {
                return;
            }
            seenFieldsetIds.add(element.id);
            const members: Array<{ element: any; index: number }> = [];
            elements.forEach((candidate, candidateIndex) => {
                if (candidate.type !== 'fieldset' && candidate.fieldsetId === element.id) {
                    members.push({ element: candidate, index: candidateIndex });
                }
            });
            blocks.push({ kind: 'fieldset', head: element, members, index });

            return;
        }

        // Member of a known fieldset → already collected above.
        if (element.fieldsetId && heads.has(element.fieldsetId)) {
            return;
        }

        blocks.push({ kind: 'single', element, index });
    });

    return blocks;
});

const parsedFormSubmit = computed(() => {
    if (typeof props.form_submit === 'string') {
        try {
            return normalizeFormSubmit(JSON.parse(props.form_submit));
        } catch (error) {
            console.error('Error parsing form_submit:', error);
            return normalizeFormSubmit(null);
        }
    }
    return normalizeFormSubmit(props.form_submit);
});

const parsedValidationMessages = computed(() => {
    if (!props.form_validation_messages) return null;

    if (typeof props.form_validation_messages === 'string') {
        try {
            return JSON.parse(props.form_validation_messages);
        } catch (error) {
            console.error('Error parsing form_validation_messages:', error);
            return null;
        }
    }
    return props.form_validation_messages;
});

// Add/Remove button labels for repeatable fieldsets. Server resolves the
// translation keys (vfb.fieldset.add/remove) and passes them in; English is
// the fallback when the prop is absent.
const parsedFieldsetLabels = computed<{ add: string; remove: string }>(() => {
    const fallback = { add: 'Add', remove: 'Remove' };
    if (!props.form_fieldset_labels) {
        return fallback;
    }
    let raw: any = props.form_fieldset_labels;
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        } catch (error) {
            console.error('Error parsing form_fieldset_labels:', error);
            return fallback;
        }
    }
    return {
        add: (raw && raw.add) || fallback.add,
        remove: (raw && raw.remove) || fallback.remove,
    };
});

// Labels of the file input (upload trigger, remove button per file). Server
// resolves the translation keys (vfb.file.upload/remove); English is the fallback.
const parsedFileLabels = computed<{ upload: string; remove: string }>(() => {
    const fallback = { upload: 'Upload file', remove: 'Remove' };
    if (!props.form_file_labels) {
        return fallback;
    }
    let raw: any = props.form_file_labels;
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        } catch (error) {
            console.error('Error parsing form_file_labels:', error);
            return fallback;
        }
    }
    return {
        upload: (raw && raw.upload) || fallback.upload,
        remove: (raw && raw.remove) || fallback.remove,
    };
});

// Computed property to handle string to object conversion for field overrides
const parsedFieldOverrides = computed(() => {
    if (!props.form_fields_override) return null;

    if (typeof props.form_fields_override === 'string') {
        try {
            return JSON.parse(props.form_fields_override);
        } catch (error) {
            console.error('Error parsing form_fields_override:', error);
            return null;
        }
    }
    return props.form_fields_override;
});

// Template ref to the FormContainer, which exposes the form-validation v3
// FormValidator instance as `fv`.
const formContainerRef = ref<any>(null);

// Re-scans the form DOM so form-validation v3 registers inputs of dynamically
// added fieldset instances (and drops removed ones). Runs on the next tick so
// the new instance is already in the DOM. `fv` may be exposed wrapped or
// unwrapped depending on Vue's exposed-ref handling, so we resolve both.
const refreshValidator = () => {
    const exposedFv = formContainerRef.value?.fv;
    const validator = exposedFv && typeof exposedFv.refresh === 'function' ? exposedFv : exposedFv?.value;
    if (validator && typeof validator.refresh === 'function') {
        validator.refresh();
    }
};

const prefillData = ref<Record<string, string | string[]>>({});
const validFormFields = ref<Set<string>>(new Set());
// Holds the current values for all form fields. These are initially populated
// from prefillData (if available) via getPrefillValue and then updated through
// user interaction.
const formFieldValues = ref<Record<string, any>>({});

// Reactive refs for field types that update when overrides are loaded
const currentFormFieldTypes = ref(getFormFieldTypes());
const currentAdditionalFieldTypes = ref(getAdditionalFieldTypes());

// Watch for changes in field overrides and apply them immediately
watch(parsedFieldOverrides, (newOverrides) => {
    if (newOverrides) {
        loadFieldOverrides(newOverrides);
        currentFormFieldTypes.value = getFormFieldTypes();
        currentAdditionalFieldTypes.value = getAdditionalFieldTypes();
    }
}, { immediate: true });

// Helper to fetch prefill data from the given URL
const fetchPrefillData = async (url: string) => {
    try {
        const response = await fetch(url);
        const data = await response.json();

        if (data.success) {
            prefillData.value = data.prefill_data || {};

            // Check for prefill keys that don't exist as form fields
            Object.keys(prefillData.value).forEach(prefillKey => {
                if (!validFormFields.value.has(prefillKey)) {
                    console.error(`Prefill data contains key "${prefillKey}" that does not exist in form fields`);
                }
            });

            // When new prefill data is loaded, reset all cached field values so that
            // subsequent calls to getPrefillValue will use the updated prefillData.
            formFieldValues.value = {};
        } else {
            console.error('Prefill request failed:', data);
        }
    } catch (error) {
        console.error('Error fetching prefill data:', error);
    }
};

onMounted(async () => {
    if (parsedFormElements.value) {
        parsedFormElements.value.forEach(element => {
            if ('name' in element && element.name) {
                validFormFields.value.add(element.name);
            }
        });
    }

    if (props.form_prefill) {
        await fetchPrefillData(props.form_prefill);
    }

    // Back-navigation after a classic submit restores the page from the
    // bfcache — nothing is in flight any more, so unlock the button.
    window.addEventListener('pageshow', onPageShow);
});

onUnmounted(() => {
    window.removeEventListener('pageshow', onPageShow);
});

// If the prefill URL changes at runtime (e.g. due to reactive binding),
// re-fetch the prefill data so that it can be used by getPrefillValue.
watch(() => props.form_prefill, async (newUrl) => {
    if (newUrl) {
        await fetchPrefillData(newUrl);
    }
});


const getPrefillValue = (fieldName: string, defaultValue: any = null) => {
    // Initialize field value if not already set. Values are reset whenever
    // new prefillData is loaded (see fetchPrefillData), so this method will
    // always use the latest prefill data after a prefill request.
    if (!(fieldName in formFieldValues.value)) {
        if (prefillData.value && fieldName in prefillData.value) {
            const prefillValue = prefillData.value[fieldName];

            if (prefillValue !== null && prefillValue !== undefined) {
                formFieldValues.value[fieldName] = prefillValue;
            } else {
                formFieldValues.value[fieldName] = defaultValue;
            }
        } else {
            formFieldValues.value[fieldName] = defaultValue;
        }
    }
    return formFieldValues.value[fieldName];
};

const updateFieldValue = (fieldName: string, value: any) => {
    formFieldValues.value[fieldName] = value;
};

// When a condition flips an element's visibility (v-if add/remove) or required
// state, the form-validation v3 instance must re-scan the DOM so it registers
// or drops the affected input. A compact signature of every conditioned
// element's [visible, required] state drives a single refresh on change.
const conditionStateSignature = computed(() => {
    const els = parsedFormElements.value || [];
    return els
        .filter((el: any) => el && el.conditions)
        .map((el: any) => `${el.name}:${isElementVisible(el) ? 1 : 0}${isElementRequired(el) ? 1 : 0}`)
        .join('|');
});
watch(conditionStateSignature, () => {
    nextTick(() => refreshValidator());
});

const getClassesForFieldType = (type) => {
    const fieldType = currentFormFieldTypes.value.find(field => field.value === type);
    return fieldType?.classes || {
        wrapper: 'form-group js-form-validation__group',
        container: 'position-relative',
        input: 'form-control',
        label: '',
        help: '',
        errors: '',
    };
};

const getClassesForAdditionalFieldType = (type) => {
    const fieldType = currentAdditionalFieldTypes.value.find(field => field.value === type);
    return fieldType?.classes || { title: '' };
};

const getTagTypeForAdditionalFieldType = (type) => {
    const fieldType = currentAdditionalFieldTypes.value.find(field => field.value === type);
    return fieldType?.elementType || 'div';
};


</script>

<style lang="scss">
.vfb .vfb-form {
    color: inherit;
}

/* purgecss start ignore */
// Ajax loading overlay — these class names come from this package's <style>,
// not from a host base-style block, so the host's PurgeCSS would strip them
// (the package .vue sources are not in the host content scan). The ignore range
// keeps them; all rules below are outside @media, so it is plugin-order
// independent (see vue-forms/docs/host-base-styles.md).         nb
// Anchor the ajax loading overlay to the form wrapper.
.form-builder {
    position: relative;
}

.form-builder .vfb-loading-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(255, 255, 255, 0.8);
}

// Spinner styling is SELF-CONTAINED here on purpose: the Symfony-UX mount
// overlay's CSS lives in the Twig placeholder (<style> inside the mount div),
// which Vue REPLACES on mount — so after mount those .form-builder-loading__*
// rules no longer exist. We re-declare the same look (matching the mount
// overlay) so the ajax loading overlay actually renders.
.form-builder .vfb-loading-overlay {
    .form-builder-loading__content {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .form-builder-loading__spinner {
        margin: 20px auto;
        width: 70px;
        text-align: center;
    }

    .form-builder-loading__dot {
        width: 12px;
        height: 12px;
        background-color: var(--color-primary, #0d6efd);
        border-radius: 100%;
        display: inline-block;
        margin: 0 3px;
        animation: vfb-spinner-bounce 1.4s infinite ease-in-out both;
    }

    .form-builder-loading__dot--1 { animation-delay: -0.32s; }
    .form-builder-loading__dot--2 { animation-delay: -0.16s; }

    .form-builder-loading__text {
        margin-top: 10px;
        color: #64748b;
        font-size: 14px;
    }
}

@keyframes vfb-spinner-bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
}
/* purgecss end ignore */

.vfb .vfb-field:has(.vfb-check-input.is-invalid) .vfb-label,
.vfb .vfb-checkbox-group:has(.vfb-check-input.is-invalid) .vfb-legend,
.vfb .vfb-choice-group:has(.vfb-check-input.is-invalid) .vfb-legend {
    color: var(--vfb-color-error);
}
</style>
